/** AFAS weekday codes (legacy bluebox AfasTimeTable). */
const WEEKDAY_CODES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const LANGE_DAY_TO_AFAS = {
    monday: 'Mo',
    tuesday: 'Tu',
    wednesday: 'We',
    thursday: 'Th',
    friday: 'Fr',
    saturday: 'Sa',
    sunday: 'Su',
};

export const weekdayCodeFromDate = (datum) => {
    if (!datum) {
        return null;
    }

    const parsed = new Date(String(datum).slice(0, 10));
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    return WEEKDAY_CODES[parsed.getDay()];
};

const resolveWtfHours = (dagdeel) => {
    const raw = dagdeel?.wtfValue ?? dagdeel?.wtf;
    if (raw == null || raw === '') {
        return null;
    }

    const numeric = Number(raw);
    return Number.isFinite(numeric) ? numeric : null;
};

/**
 * Preview AFAS contract/timetable fields for one dagdeel (bluebox reference).
 *
 * @param {object} dagdeel
 * @param {object} verzoek ClooserMeta.verzoek snapshot
 */
export const buildDagdeelAfasPreview = (dagdeel, verzoek = {}) => {
    const dayCode = weekdayCodeFromDate(dagdeel?.datum);
    const wtfHours = resolveWtfHours(dagdeel);
    const timetableFields = {};

    if (dayCode) {
        timetableFields[`Wd${dayCode}`] = wtfHours != null && wtfHours !== 0;
        timetableFields[`Ft${dayCode}`] =
            wtfHours != null && wtfHours !== 0 ? wtfHours : null;
    }

    return {
        dagdeelId: dagdeel?.id ?? null,
        datum: dagdeel?.datum ?? null,
        shift: dagdeel?.shift ?? null,
        dayType: dagdeel?.dayTypeAbbr || dagdeel?.dayTypeName || null,
        wtf: dagdeel?.wtf ?? null,
        wtfValue: dagdeel?.wtfValue ?? null,
        afasContract: {
            DaBe: dagdeel?.datum ?? null,
            DaEn: dagdeel?.datum ?? null,
            DvbSubs: verzoek.afwezigeMdwNr ?? verzoek.vervangt ?? null,
        },
        afasTimeTable: {
            '@DaBg': dagdeel?.datum ?? null,
            Fields: timetableFields,
        },
    };
};

/**
 * Aggregate Ft per weekday across all dagdelen (legacy sums per week batch).
 *
 * @param {Array<object>} dagdelen
 */
export const aggregateTimeTableFields = (dagdelen = []) => {
    const feetByDay = {};

    dagdelen.forEach((dagdeel) => {
        const dayCode = weekdayCodeFromDate(dagdeel?.datum);
        const wtfHours = resolveWtfHours(dagdeel);
        if (!dayCode || wtfHours == null) {
            return;
        }

        feetByDay[dayCode] = (feetByDay[dayCode] ?? 0) + wtfHours;
    });

    return buildFullTimeTableFields(feetByDay);
};

/**
 * Langdurig rooster: WTF pattern for monday…sunday over the whole period.
 *
 * @param {Array<object>} days
 */
export const aggregateLangeTimeTableFields = (days = []) => {
    const feetByDay = {};

    days.forEach((day) => {
        const key = String(day?.dayKey ?? day?.id ?? '').toLowerCase();
        const afasDay = LANGE_DAY_TO_AFAS[key];
        if (!afasDay) {
            return;
        }
        const wtf = Number(day?.wtf ?? 0);
        if (!Number.isFinite(wtf)) {
            return;
        }
        feetByDay[afasDay] = wtf;
    });

    return buildFullTimeTableFields(feetByDay);
};

const buildFullTimeTableFields = (feetByDay = {}) => {
    const fields = {
        StPa: false,
        Spec: false,
    };
    let total = 0;

    WEEKDAY_CODES.forEach((dayCode) => {
        const hours = Number(feetByDay[dayCode] ?? 0);
        const active = hours !== 0;
        fields[`Wd${dayCode}`] = active;
        if (active) {
            total += hours;
        }
    });

    fields.Ft = total > 0 ? Number(total.toFixed(4)) : 0;
    fields.FtSv = false;

    WEEKDAY_CODES.forEach((dayCode) => {
        const hours = Number(feetByDay[dayCode] ?? 0);
        fields[`Ft${dayCode}`] = hours !== 0 ? Number(hours.toFixed(4)) : null;
    });

    return fields;
};

/**
 * Build debug preview for a synchronisatie batch row.
 *
 * @param {object|null|undefined} row
 */
export const buildBatchAfasPreview = (row) => {
    const meta = row?.clooserMeta ?? {};
    const dagdelen = Array.isArray(meta.dagdelen) ? meta.dagdelen : [];
    const verzoek = meta.verzoek ?? {};
    const prolonged = row?.clooserProlonged ?? null;

    if (prolonged) {
        const days = Array.isArray(prolonged.days) ? prolonged.days : [];

        return {
            mode: 'langdurig',
            dagdelenCount: 0,
            perDagdeel: [],
            period: {
                beginDatum: prolonged.beginDatum ?? null,
                eindDatum: prolonged.eindDatum ?? null,
                prolongedId: prolonged.prolongedId ?? null,
                days,
            },
            aggregatedTimeTable: {
                Fields: aggregateLangeTimeTableFields(days),
            },
            verzoek,
            extra: meta.extra ?? null,
        };
    }

    return {
        mode: 'week',
        dagdelenCount: dagdelen.length,
        perDagdeel: dagdelen.map((dagdeel) => buildDagdeelAfasPreview(dagdeel, verzoek)),
        aggregatedTimeTable: {
            Fields: aggregateTimeTableFields(dagdelen),
        },
        verzoek,
        extra: meta.extra ?? null,
        period: null,
    };
};
